import React, { useState } from "react";

import {
    Link,
    useParams,
    useSearchParams,
    useRevalidator,
} from "@remix-run/react";

import {
    PART_MEDITATION_STRIP_SURFACE,
    PartMeditationButton,
} from "~/features/meditations/components/part-meditation-button";
import {
    ChevronRight,
    HelpCircle,
    Link2,
    CheckCircle2,
    Sparkles,
} from "lucide-react";
import { toast } from "sonner";

import { SessionSummaryMarkdown } from "~/components/session/session-summary-markdown";
import { Button } from "~/components/ui/button";
import { H3, P } from "~/components/ui/typography";
import type {
    TPartInteractionWithImage,
    TherapeuticStage,
} from "~/database/types";
import type { TClientSettings } from "~/database/types/practitioner-types";
import { PartImage } from "~/features/parts/components/part-detail/part-image";
import { PartInteractionHistoryPopover } from "~/features/parts/components/part-interaction-history-popover";
import { PartSimilarityDialog } from "~/features/parts/components/part-similarity-dialog";
import { THERAPEUTIC_STAGE_CONFIG } from "~/features/parts/constants";
import { getPartType } from "~/features/parts/utils/part-types";
import { trackEvent } from "~/lib/analytics";
import { useApiClient } from "~/hooks/use-api-client";
import { useSession } from "~/hooks/use-session";

import { cn, getSessionPartInteractionUrl } from "../../lib/utils";

// AIDEV-NOTE: Get part type config from shared utility, fallback to HelpCircle
function getPartTypeConfig(type: string | null) {
    if (!type) return { icon: HelpCircle, label: "Part" };
    const partType = getPartType(type);
    return partType || { icon: HelpCircle, label: "Other" };
}

// AIDEV-NOTE: Get therapeutic stage config, returns null if stage not set
// Only shows stage when we have medium/high confidence from LLM inference
function getTherapeuticStageConfig(part: TPartInteractionWithImage) {
    if (part.therapeuticStage) {
        return THERAPEUTIC_STAGE_CONFIG[
            part.therapeuticStage as TherapeuticStage
        ];
    }
    // Return null when no stage is set - UI should hide stage badge
    return null;
}

// AIDEV-NOTE: Sort order for focusWeight - substantial parts should appear first
const FOCUS_WEIGHT_ORDER: Record<string, number> = {
    substantial: 0,
    present: 1,
    brief: 2,
};

/**
 * Sort part interactions by focusWeight within category
 * Order: substantial > present > brief > null/undefined
 */
function sortByFocusWeight(
    partInteractions: TPartInteractionWithImage[],
): TPartInteractionWithImage[] {
    return [...partInteractions].sort((a, b) => {
        const aWeight = a.focusWeight
            ? FOCUS_WEIGHT_ORDER[a.focusWeight] ?? 3
            : 3;
        const bWeight = b.focusWeight
            ? FOCUS_WEIGHT_ORDER[b.focusWeight] ?? 3
            : 3;
        return aWeight - bWeight;
    });
}

// AIDEV-NOTE: Shadow applied via inline style (NOT the tailwind shadow-card token).
// Root cause of the old "shadow-card renders as none" mystery: the token collided
// with the `card` COLOR, whose shadow-color utility won the cascade (fixed via the
// boxShadowColor exclusion in tailwind.config.ts). These inline values are
// intentionally stronger than the token. Rest = exact Claude design; hover = lift.
const PART_CARD_SHADOW =
    "0 1px 2px rgba(27, 67, 50, 0.06), 0 8px 20px rgba(27, 67, 50, 0.10)";
const PART_CARD_SHADOW_HOVER =
    "0 2px 6px rgba(27, 67, 50, 0.08), 0 14px 30px rgba(27, 67, 50, 0.14)";
// AIDEV-NOTE: The card sits ON the meditation strip, which tucks up behind its rounded
// bottom corners. The seam between them is this card's own shadow falling onto the strip,
// which is why it needs a tight downward layer the page-level lift does not have — and why
// the strip carries no inset shadow of its own. An inset would rule a straight line across
// the corners; a cast shadow curves with them.
const PART_CARD_SHADOW_STACKED =
    "0 1px 2px rgba(27, 67, 50, 0.025), 0 5px 8px -5px rgba(27, 67, 50, 0.05), 0 8px 20px rgba(27, 67, 50, 0.045)";
const PART_CARD_SHADOW_STACKED_HOVER =
    "0 2px 6px rgba(27, 67, 50, 0.035), 0 6px 10px -5px rgba(27, 67, 50, 0.06), 0 14px 30px rgba(27, 67, 50, 0.065)";

function DesktopPartCardButton({
    children,
    onClick,
    isSubstantial,
    dataTourId,
    hidden,
}: {
    children: React.ReactNode;
    onClick: () => void;
    isSubstantial: boolean;
    dataTourId?: string;
    hidden?: boolean;
}) {
    const [hovered, setHovered] = useState(false);
    return (
        <button
            className={cn(
                "w-full rounded-[18px] border border-brand-forest-100 bg-white p-[18px] text-left transition-shadow duration-200",
                "hidden overflow-hidden lg:block",
                // Above the strip, which tucks up behind these corners.
                "relative z-[1]",
                hidden && "hidden",
                isSubstantial &&
                    "relative pl-4 before:absolute before:inset-y-0 before:left-0 before:w-1 before:bg-brand-terra-400",
            )}
            style={{
                boxShadow: hovered
                    ? PART_CARD_SHADOW_STACKED_HOVER
                    : PART_CARD_SHADOW_STACKED,
            }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onClick={onClick}
            data-tour-id={dataTourId}
        >
            {children}
        </button>
    );
}

interface PartsListProps {
    partInteractions: TPartInteractionWithImage[];
    onPartClick?: (partInteraction: TPartInteractionWithImage) => void;
    effectiveSettings?: TClientSettings | null;
    showPartImages?: boolean;
    /** The session OWNER's meditation preference — not the viewer's. */
    meditationsEnabledForClient?: boolean;
    // AIDEV-NOTE: confidence accepts categorical ("high"/"medium"/"low") or legacy numeric
    partSimilarities?: Record<
        string,
        Array<{
            partId: string;
            confidence: "high" | "medium" | "low" | number;
            reason: string;
        }>
    >;
    isPractitioner?: boolean; // AIDEV-NOTE: Show part type and therapeutic stage for practitioners
    // AIDEV-NOTE: Show practitioner clinical notes per part - only on client dashboard, not session notes
    showPractitionerNotes?: boolean;
    /** Use smaller part images */
    smallImages?: boolean;
    /** Render description as a full-width row below the image+title row */
    descriptionBelow?: boolean;
}

export function PartsList({
    partInteractions,
    onPartClick,
    effectiveSettings,
    showPartImages = true,
    meditationsEnabledForClient,
    partSimilarities,
    isPractitioner = false,
    showPractitionerNotes = false,
    smallImages = false,
    descriptionBelow = false,
}: PartsListProps) {
    const { sessionId } = useParams();
    const [searchParams] = useSearchParams();
    const sessionToken = searchParams.get("sessionToken");
    const { isConsultantViewer } = useSession(sessionId || "");
    // AIDEV-NOTE: Safe for SSR - useSearchParams returns consistent values during hydration.
    // isPractitioner is already corrected for consultants by the caller
    // (SessionParts passes isPractitioner && !isReadOnlyViewer), but this file
    // also gates some things on isPublicSession directly — self-computed like
    // TitleEditor/DateEditor, so it needs the same consultant check (Adam,
    // 2026-09-03).
    const isPublicSession = !!sessionToken || isConsultantViewer;
    const revalidator = useRevalidator();
    const apiClient = useApiClient();
    const [selectedPartForSimilarity, setSelectedPartForSimilarity] = useState<
        string | null
    >(null);
    const [isSimilarityDialogOpen, setIsSimilarityDialogOpen] = useState(false);
    const [isConfirmingAutoLinks, setIsConfirmingAutoLinks] = useState(false);

    // AIDEV-NOTE: Find auto-linked unconfirmed parts for bulk confirmation UI
    const autoLinkedUnconfirmedParts = partInteractions.filter(
        (pi) => pi.linkStatus === "automatic_unconfirmed",
    );

    const handleConfirmAllAutoLinks = async () => {
        if (autoLinkedUnconfirmedParts.length === 0) return;

        setIsConfirmingAutoLinks(true);
        try {
            const response = await apiClient.post("/parts/confirm-auto-link", {
                partInteractionIds: autoLinkedUnconfirmedParts.map(
                    (pi) => pi.id,
                ),
            });

            if (response.ok) {
                const data = await response.json();
                toast.success(
                    `Confirmed ${data.confirmed} auto-linked part(s)`,
                );
                revalidator.revalidate();
            } else {
                const errorData = await response.json();
                toast.error(
                    errorData.error || "Failed to confirm auto-linked parts",
                );
            }
        } catch (error) {
            toast.error("Failed to confirm auto-linked parts");
        } finally {
            setIsConfirmingAutoLinks(false);
        }
    };

    // AIDEV-NOTE: Meditations lost their session tab — the way into one is now the part you
    // want to sit with. Rendered per breakpoint so each card variant gets its own strip
    // without duplicating the markup. Only part check-ins for now; every other meditation
    // type stays withheld from this surface.
    // AIDEV-NOTE: `withCard` slides the strip UP behind the part card by exactly the
    // card's corner radius and pads the same amount back, so the card keeps all four of
    // its rounded corners and the strip runs on underneath it — the two read as one object
    // resting on another (Brent, 2026-09-09: the flush/squared-corner variant was tried
    // and reverted). `empty:hidden` covers the case where PartMeditationButton renders
    // null (flag off, a share link with no cached meditation, or a non-part entry).
    // `withCard` false = the card is hidden (`onPartClick` absent), so the strip stands
    // alone as a fully rounded box. The wrapper carries the strip's OWN surface —
    // PART_MEDITATION_STRIP_SURFACE, shared with the row — so the corner notches reveal
    // the strip rather than a wedge of something else.
    const meditationCtaFor = (
        partInteraction: TPartInteractionWithImage,
        breakpointClass: string,
        withCard: boolean,
        index: number,
    ) =>
        sessionId ? (
            <div
                // AIDEV-NOTE: Tour anchor on the FIRST card only, same as first-part-card.
                // Meditations lost their tab, so this strip is the only route to one from a
                // session and the tour has to be able to point at it.
                data-tour-id={index === 0 ? "first-part-meditation" : undefined}
                className={cn(
                    "group/strip overflow-hidden border border-brand-forest-100 transition-colors empty:hidden hover:bg-brand-forest-50",
                    withCard
                        ? "-mt-[18px] rounded-b-[18px] border-t-0 pt-[18px]"
                        : "rounded-[18px]",
                    PART_MEDITATION_STRIP_SURFACE,
                    breakpointClass,
                )}
                style={{ boxShadow: PART_CARD_SHADOW }}
            >
                <PartMeditationButton
                    variant="strip"
                    sessionId={sessionId}
                    partInteractionId={partInteraction.id}
                    partName={partInteraction.name ?? "this part"}
                    partType={partInteraction.type}
                    isPublicSession={isPublicSession}
                    meditationsEnabledForSubject={meditationsEnabledForClient}
                />
            </div>
        ) : null;

    const handlePartClick = (partInteraction: TPartInteractionWithImage) => {
        trackEvent("viewed a part", {
            partId: partInteraction.id,
        });

        if (onPartClick) {
            onPartClick(partInteraction);
        }
    };

    const handleSimilarityClick = (partInteractionId: string) => {
        setSelectedPartForSimilarity(partInteractionId);
        setIsSimilarityDialogOpen(true);
    };

    return (
        <div className="space-y-4">
            {/* AIDEV-NOTE: Auto-linked parts confirmation banner - hidden for now
            {autoLinkedUnconfirmedParts.length > 0 && (
                    <div className="mx-2 mb-4 rounded-lg border border-primary/20 bg-primary/5 p-4 md:mx-4">
                        <div className="flex items-center justify-between gap-4">
                            <div className="flex items-center gap-2">
                                <Sparkles className="size-5 text-primary" />
                                <div>
                                    <P className="font-medium">
                                        {autoLinkedUnconfirmedParts.length}{" "}
                                        auto-linked{" "}
                                        {autoLinkedUnconfirmedParts.length === 1
                                            ? "part"
                                            : "parts"}{" "}
                                        to confirm
                                    </P>
                                    <P className="text-sm text-muted-foreground">
                                        These parts were automatically matched
                                        with high confidence
                                    </P>
                                </div>
                            </div>
                            <Button
                                size="sm"
                                onClick={handleConfirmAllAutoLinks}
                                disabled={isConfirmingAutoLinks}
                            >
                                {isConfirmingAutoLinks ? (
                                    "Confirming..."
                                ) : (
                                    <>
                                        <CheckCircle2 className="mr-2 size-4" />
                                        Confirm All
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                )}
            */}

            {sortByFocusWeight(partInteractions).map(
                (partInteraction, index) => {
                    const isAutoLinked =
                        partInteraction.linkStatus === "automatic_unconfirmed";

                    // AIDEV-NOTE: Part type config — icon + base label. Parenthetical
                    // qualifiers (e.g. "Protector (Firefighter)") are stripped. When set
                    // (practitioner only), the therapeutic stage renders to the RIGHT of
                    // the type; the cluster sits UNDER the name at every breakpoint
                    // (Brent, 2026-09-09 — it used to be right-justified on desktop).
                    const typeConfig = getPartTypeConfig(partInteraction.type);
                    const TypeIcon = typeConfig.icon;
                    const baseTypeLabel = (partInteraction.type || "Part")
                        .replace(/\s*\([^)]*\)/g, "")
                        .trim();
                    const stageConfig = isPractitioner
                        ? getTherapeuticStageConfig(partInteraction)
                        : null;

                    // AIDEV-NOTE: Show canonical part.name so renames from session notes sync to the part map
                    // AIDEV-NOTE: Stacked at every breakpoint: the name gets the full row
                    // and the stage/type cluster sits underneath it, so a long name never
                    // wraps around a shrink-0 cluster ("The / Retriever").
                    const title = (
                        <div className="mb-2 flex flex-col items-start gap-1">
                            <H3 className="mb-0 flex items-start space-x-2 text-brand-terra-700 font-semibold">
                                <span>
                                    {partInteraction.part?.name ||
                                        partInteraction.name}
                                </span>
                                <ChevronRight className="mt-1.5 hidden size-6 shrink-0 lg:block" />
                            </H3>
                            {isPractitioner && (
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <span className="inline-flex items-center gap-1">
                                        <TypeIcon className="size-4" />
                                        {baseTypeLabel}
                                    </span>
                                    {stageConfig && (
                                        <>
                                            <span className="text-muted-foreground/50">
                                                ·
                                            </span>
                                            <span>{stageConfig.label}</span>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                    // Check if this part has similarity matches
                    const similarityMatches =
                        partSimilarities?.[partInteraction.id];
                    const hasSimilarities =
                        similarityMatches && similarityMatches.length > 0;

                    const content = (
                        <>
                            <div className="space-y-2">
                                {/* AIDEV-NOTE: A two-column grid rather than a flex row, so
                                    the body can change WHICH columns it occupies per
                                    breakpoint without being rendered twice. The image sits
                                    in the RIGHT column. On a phone only the part's NAME sits
                                    beside it; the description and the similarity/history row
                                    span both columns underneath, where they get the full
                                    card width instead of a narrow gutter next to the avatar.
                                    From lg the body tucks back under the title in column
                                    one, beside the image. `descriptionBelow` keeps it
                                    full-width at every size. */}
                                <div className="grid grid-cols-[1fr_auto] items-start gap-x-4 lg:gap-x-2">
                                    {showPartImages && (
                                        <div
                                            className={cn(
                                                "col-start-2 row-start-1 shrink-0",
                                                // On lg the body sits beside the image in
                                                // column one, so the image has to span both
                                                // rows or it would only ever be as tall as
                                                // the title and push the description under
                                                // itself.
                                                !descriptionBelow &&
                                                    "lg:row-span-2",
                                            )}
                                            data-tour-feature="part-image"
                                            data-tour-id={
                                                index === 0
                                                    ? "first-part-image"
                                                    : undefined
                                            }
                                        >
                                            <PartImage
                                                image={
                                                    partInteraction.part_image
                                                }
                                                size={smallImages ? "sm" : "md"}
                                            />
                                        </div>
                                    )}
                                    {/* AIDEV-NOTE: Type + stage live together in the cluster
                                        under the name (type left of stage). */}
                                    {/* AIDEV-NOTE: `self-center` against the grid's
                                        `items-start`. Row one is as tall as the image, so a
                                        one- or two-line name sits level with the middle of
                                        the avatar instead of hanging off its top edge; a
                                        name long enough to out-grow the image sets the row
                                        height itself and centring becomes a no-op. On lg the
                                        image spans both rows, so row one is only as tall as
                                        the title and this does nothing there either. */}
                                    <div className="col-start-1 row-start-1 flex min-w-0 flex-col self-center pr-2 lg:pr-4">
                                        {title}
                                    </div>
                                    <div
                                        className={cn(
                                            "col-span-2 col-start-1 row-start-2 mt-2 flex min-w-0 flex-col",
                                            !descriptionBelow &&
                                                "lg:col-span-1 lg:mt-0 lg:pr-4",
                                        )}
                                    >
                                        {showPractitionerNotes &&
                                        partInteraction.practitionerNotes ? (
                                            <SessionSummaryMarkdown
                                                markdown={
                                                    partInteraction.practitionerNotes
                                                }
                                                className="text-sm"
                                            />
                                        ) : (
                                            <SessionSummaryMarkdown
                                                markdown={
                                                    partInteraction.description ??
                                                    ""
                                                }
                                            />
                                        )}
                                        {/* AIDEV-NOTE: Show interaction history for linked parts */}
                                        {partInteraction.partId &&
                                            sessionId && (
                                                <PartInteractionHistoryPopover
                                                    partId={
                                                        partInteraction.partId
                                                    }
                                                    currentSessionId={sessionId}
                                                    currentInteractionId={
                                                        partInteraction.id
                                                    }
                                                    partName={
                                                        partInteraction.part
                                                            .name
                                                    }
                                                    currentInteractionName={
                                                        partInteraction.name
                                                    }
                                                    isAutoLinked={isAutoLinked}
                                                    currentPartDetails={{
                                                        description:
                                                            partInteraction.description,
                                                        recap: partInteraction.recap,
                                                        quote: partInteraction.quote,
                                                        attributes:
                                                            partInteraction.attributes,
                                                    }}
                                                    similarityData={
                                                        partSimilarities?.[
                                                            partInteraction.id
                                                        ]?.[0]
                                                    }
                                                    // AIDEV-NOTE: Prefetched history data eliminates N+1 API calls
                                                    prefetchedHistory={
                                                        partInteraction.interactionHistory
                                                    }
                                                    isPublicSession={
                                                        isPublicSession
                                                    }
                                                />
                                            )}
                                        {/* AIDEV-NOTE: Hidden on mobile - similarity modal not optimized for small screens */}
                                        {/* AIDEV-NOTE: Hide potential matches button for public sessions */}
                                        {/* AIDEV-NOTE: EVO-1222 - Hide button when user confirmed "this is a new part" (linkStatus=manual_dismissed) */}
                                        {hasSimilarities &&
                                            !partInteraction.isMatched &&
                                            partInteraction.linkStatus !==
                                                "manual_dismissed" &&
                                            !isPublicSession && (
                                                <div className="mt-2 hidden md:block">
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        className="gap-2"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            handleSimilarityClick(
                                                                partInteraction.id,
                                                            );
                                                        }}
                                                    >
                                                        <Link2 className="size-4" />
                                                        {
                                                            similarityMatches.length
                                                        }
                                                        {" potential "}
                                                        {similarityMatches.length ===
                                                        1
                                                            ? "match"
                                                            : "matches"}
                                                    </Button>
                                                </div>
                                            )}
                                    </div>
                                </div>
                            </div>
                        </>
                    );

                    const isSubstantial =
                        partInteraction.focusWeight === "substantial";

                    // AIDEV-NOTE: The interaction's OWN sessionId, falling back to the
                    // route's. This list also renders on the dashboard
                    // (most-recent-session-card), where there is no :sessionId param — the
                    // route value is undefined there and a non-null assertion was quietly
                    // building /sessions/undefined/parts/…. Neither is guaranteed, so the
                    // card drops its link rather than pointing at a URL that cannot resolve.
                    const cardSessionId =
                        partInteraction.sessionId ?? sessionId;
                    const cardClassName = cn(
                        "relative z-[1] block overflow-hidden rounded-[18px] border border-brand-forest-100 bg-white p-[18px]",
                        !onPartClick && "hidden",
                        isSubstantial &&
                            "relative pl-4 before:absolute before:inset-y-0 before:left-0 before:w-1 before:bg-brand-terra-400",
                    );
                    const cardStyle = { boxShadow: PART_CARD_SHADOW_STACKED };
                    const cardTourId =
                        index === 0 ? "first-part-card" : undefined;

                    return (
                        <React.Fragment key={partInteraction.id}>
                            {/* AIDEV-NOTE: Mobile part cards mirror the desktop
                            card styling (white bg, rounded corners, soft lift)
                            instead of the previous bare divider list. */}
                            <div className="lg:hidden">
                                {cardSessionId ? (
                                    <Link
                                        className={cardClassName}
                                        style={cardStyle}
                                        to={getSessionPartInteractionUrl(
                                            partInteraction.id,
                                            cardSessionId,
                                        )}
                                        onClick={() =>
                                            handlePartClick(partInteraction)
                                        }
                                        data-tour-id={cardTourId}
                                    >
                                        {content}
                                    </Link>
                                ) : (
                                    <div
                                        className={cardClassName}
                                        style={cardStyle}
                                        data-tour-id={cardTourId}
                                    >
                                        {content}
                                    </div>
                                )}
                                {/* AIDEV-NOTE: The meditation CTA sits OUTSIDE the card's own
                                link/button — nesting a second interactive inside them is
                                invalid and would make the whole card ambiguous to click.
                                It closes the card visually instead, carrying the bottom
                                radius and border the body gives up. Mobile copy of the
                                strip; the desktop one follows the button below. */}
                                {meditationCtaFor(
                                    partInteraction,
                                    "",
                                    !!onPartClick,
                                    index,
                                )}
                            </div>
                            <div className="hidden lg:block">
                                <DesktopPartCardButton
                                    onClick={() =>
                                        handlePartClick(partInteraction)
                                    }
                                    isSubstantial={isSubstantial}
                                    hidden={!onPartClick}
                                    dataTourId={
                                        index === 0
                                            ? "first-part-card"
                                            : undefined
                                    }
                                >
                                    {content}
                                </DesktopPartCardButton>
                                {meditationCtaFor(
                                    partInteraction,
                                    "",
                                    !!onPartClick,
                                    index,
                                )}
                            </div>
                        </React.Fragment>
                    );
                },
            )}

            {/* Part Similarity Dialog for single part */}
            {sessionId && selectedPartForSimilarity && (
                <PartSimilarityDialog
                    isOpen={isSimilarityDialogOpen}
                    onOpenChange={setIsSimilarityDialogOpen}
                    sessionId={sessionId}
                    partInteractionId={selectedPartForSimilarity}
                    onComplete={(acceptedMatches) => {
                        if (acceptedMatches.length > 0) {
                            // Refresh the session data to reflect the changes
                            revalidator.revalidate();
                        }
                        setIsSimilarityDialogOpen(false);
                        setSelectedPartForSimilarity(null);
                    }}
                />
            )}
        </div>
    );
}
